import type { NotificationApiAdapter } from '../types';
import {
  getNotificationStreamToken,
  getNotificationStreamUrl,
  isNotificationStreamUpdate,
} from './notification-stream-api';

type StreamSource = Pick<
  EventTarget,
  'addEventListener' | 'removeEventListener'
> &
  Pick<EventSource, 'close'>;
type Timer = ReturnType<typeof setTimeout>;

type StreamRuntime = {
  createSource: (url: string) => StreamSource;
  setTimeout: (callback: () => void, delay: number) => Timer;
  clearTimeout: (timer: Timer) => void;
  random: () => number;
};

const browserRuntime: StreamRuntime = {
  createSource: url => new EventSource(url),
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: timer => clearTimeout(timer),
  random: () => Math.random(),
};

/** One owner for the source, token request, and reconnect timers. No token cache. */
export function createNotificationStreamConnection(
  api: NotificationApiAdapter,
  userId: string,
  onUpdate: () => void,
  runtime: StreamRuntime = browserRuntime
) {
  let active = false;
  let disposed = false;
  let generation = 0;
  let failures = 0;
  let source: StreamSource | null = null;
  let request: AbortController | null = null;
  let retryTimer: Timer | null = null;
  let stableTimer: Timer | null = null;
  let removeSourceListeners: (() => void) | null = null;

  const clearResources = () => {
    generation += 1;
    removeSourceListeners?.();
    removeSourceListeners = null;
    source?.close();
    source = null;
    request?.abort();
    request = null;
    if (retryTimer !== null) runtime.clearTimeout(retryTimer);
    if (stableTimer !== null) runtime.clearTimeout(stableTimer);
    retryTimer = null;
    stableTimer = null;
  };

  const retry = () => {
    clearResources();
    if (!active || disposed) return;

    // Close EventSource first to disable its automatic reconnect loop. Each retry
    // acquires a fresh short-lived token through the host's authenticated client.
    const baseDelay = Math.min(60_000, 1000 * 2 ** Math.min(failures, 6));
    const delay = Math.min(60_000, baseDelay * (0.8 + runtime.random() * 0.4));

    failures += 1;
    retryTimer = runtime.setTimeout(() => {
      retryTimer = null;
      void connect();
    }, delay);
  };

  const connect = async () => {
    if (!active || disposed || request || source) return;

    const currentGeneration = generation;
    const controller = new AbortController();

    request = controller;

    try {
      const token = await getNotificationStreamToken(api, controller.signal);

      if (
        controller.signal.aborted ||
        currentGeneration !== generation ||
        !active ||
        disposed
      ) {
        return;
      }

      const currentSource = runtime.createSource(
        getNotificationStreamUrl(api, userId, token)
      );
      const isCurrent = () =>
        active && !disposed && currentGeneration === generation;
      const handleUpdate = (event: Event) => {
        if (
          isCurrent() &&
          isNotificationStreamUpdate(event as MessageEvent<string>)
        ) {
          onUpdate();
        }
      };

      const handleOpen = () => {
        if (!isCurrent()) return;

        // Resync changes missed while offline/hidden. Reset backoff only after a
        // stable connection so an immediately failing server cannot cause a loop.
        onUpdate();
        if (stableTimer !== null) runtime.clearTimeout(stableTimer);
        stableTimer = runtime.setTimeout(() => {
          stableTimer = null;
          failures = 0;
        }, 30_000);
      };
      const handleError = () => {
        if (isCurrent()) retry();
      };

      source = currentSource;
      currentSource.addEventListener('open', handleOpen);
      currentSource.addEventListener('message', handleUpdate);
      currentSource.addEventListener('notification', handleUpdate);
      currentSource.addEventListener('error', handleError);
      removeSourceListeners = () => {
        currentSource.removeEventListener('open', handleOpen);
        currentSource.removeEventListener('message', handleUpdate);
        currentSource.removeEventListener('notification', handleUpdate);
        currentSource.removeEventListener('error', handleError);
      };
    } catch {
      if (!controller.signal.aborted && currentGeneration === generation)
        retry();
    } finally {
      if (request === controller) request = null;
    }
  };

  return {
    resume() {
      if (active || disposed) return;

      active = true;
      void connect();
    },
    suspend() {
      active = false;
      clearResources();
    },
    dispose() {
      disposed = true;
      active = false;
      clearResources();
    },
  };
}
